import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, Check } from "lucide-react";
import { API } from "@/lib/api";
import { addItem } from "@/lib/shopifyCart";
import { Reveal } from "@/components/motion/primitives";
import { BRAND, categorize, categoryById, SHOP_CATEGORIES, OTHER_CATEGORY } from "@/lib/shopCatalog";

export function formatMoney(amount, currency) {
  return new Intl.NumberFormat("en", { style: "currency", currency: currency || "EUR" }).format(amount);
}

/**
 * The shop catalog — live products pulled from the Shopify store (AVENO + the
 * KAI private-label range synced from Munazzo), browsable by category.
 */
export default function ShopifyProducts({ onLoaded }) {
  const [data, setData] = useState(null);
  const [added, setAdded] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get("cat") || "all";

  useEffect(() => {
    fetch(`${API}/shopify/products`)
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        onLoaded?.(d);
      })
      .catch(() => setData({ products: [], currency: "EUR" }));
  }, [onLoaded]);

  // category id -> products, in config order; only categories with products show
  const byCat = useMemo(() => {
    const map = {};
    (data?.products || []).forEach((p) => {
      const c = categorize(p);
      (map[c] = map[c] || []).push(p);
    });
    return map;
  }, [data]);

  const chips = useMemo(() => {
    const present = SHOP_CATEGORIES.filter((c) => byCat[c.id]?.length);
    if (byCat.other?.length) present.push(OTHER_CATEGORY);
    return present;
  }, [byCat]);

  if (!data || !data.products?.length) return null;

  const hasKai = data.products.some((p) => (p.vendor || "").toUpperCase() === BRAND.name);
  const visible = cat === "all" ? data.products : (byCat[cat] || []);

  const handleAdd = (product) => {
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    addItem(product, variant);
    setAdded(product.id);
    toast.success(`${product.title} added to cart`);
    setTimeout(() => setAdded(null), 1500);
  };

  return (
    <section className="py-16 md:py-20 border-b border-slate-100" data-testid="shopify-products-section">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12">
        <Reveal>
          <div className="flex items-center gap-4 mb-3 flex-wrap">
            <img
              src="/shop-images/aveno-logo.png"
              alt="AVENO logo"
              className="w-14 h-14 md:w-16 md:h-16 object-contain"
              data-testid="aveno-logo"
            />
            <div>
              <p className="font-display text-2xl md:text-3xl tracking-tight leading-none">AVENO{hasKai ? ` × ${BRAND.name}` : ""}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">
                {hasKai ? "Everyday goods + private-label essentials." : "Everyday goods. Made properly."}
              </p>
            </div>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary mb-3">My Products</p>
          <h2 className="font-display text-3xl md:text-4xl mb-3">Made by me, shipped to you.</h2>
          <p className="text-slate-600 max-w-2xl mb-8">
            My own products — secure Shopify checkout, direct from me.{" "}
            <Link to="/aveno" data-testid="aveno-story-link" className="text-primary font-semibold hover:underline">The AVENO story</Link>
            {hasKai && (
              <>{" · "}<Link to={`/${BRAND.slug}`} data-testid="kai-story-link" className="text-primary font-semibold hover:underline">The {BRAND.name} story</Link></>
            )}
          </p>
        </Reveal>

        {/* Category navigation */}
        {chips.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2 mb-8 -mx-1 px-1" data-testid="category-chips">
            <button
              onClick={() => setSearchParams(cat === "all" ? {} : {})}
              data-testid="category-chip-all"
              className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold border transition ${cat === "all" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}
            >
              All ({data.products.length})
            </button>
            {chips.map((c) => (
              <button
                key={c.id}
                onClick={() => setSearchParams({ cat: c.id })}
                data-testid={`category-chip-${c.id}`}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold border transition ${cat === c.id ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}
              >
                {c.label} ({byCat[c.id].length})
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((p) => (
            <Reveal key={p.id}>
              <div
                className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid="shopify-product-card"
              >
                <Link to={`/shop/product/${p.handle}`} data-testid="product-link" className="block">
                  {p.image && (
                    <div className="aspect-[4/3] overflow-hidden bg-slate-50 relative">
                      <img
                        src={p.image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {p.vendor && (
                        <span className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-[0.2em] text-slate-700" data-testid="product-vendor-badge">
                          {p.vendor}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-5 pb-0">
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 mb-1">{categoryById(categorize(p)).label}</p>
                    <h3 className="font-display text-lg leading-snug mb-1.5 group-hover:text-primary transition-colors">{p.title}</h3>
                    {p.description && (
                      <p className="text-sm text-slate-600 line-clamp-2 mb-4">{p.description}</p>
                    )}
                  </div>
                </Link>
                <div className="p-5 pt-0">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-slate-900" data-testid="product-price">
                      {formatMoney(p.price, data.currency)}
                    </span>
                    <button
                      onClick={() => handleAdd(p)}
                      disabled={!p.available}
                      data-testid="product-add-cart"
                      className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full transition-all active:scale-95 ${
                        added === p.id
                          ? "bg-emerald-600 text-white"
                          : "bg-primary text-white hover:bg-primary/90"
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      {added === p.id ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />}
                      {p.available ? (added === p.id ? "Added" : "Add to cart") : "Sold out"}
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        {cat !== "all" && !visible.length && (
          <p className="text-slate-500 text-sm py-10 text-center" data-testid="category-empty">
            Nothing in {categoryById(cat).label} yet — new stock lands as it is imported.
          </p>
        )}
      </div>
    </section>
  );
}
